import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', client_category: '', service_type: '', message: '', is_particulier_caf: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const contactInfo = [
    { icon: Phone, label: 'Téléphone', value: '06 84 63 28 55', href: 'tel:0684632855' },
    { icon: Mail, label: 'Email', value: 'contact@helpadn.fr', href: 'mailto:contact@helpadn.fr' },
    { icon: MapPin, label: 'Localisation', value: 'Hauts-de-France', href: null },
    { icon: Clock, label: 'Horaires', value: 'Lun-Ven: 9h-18h', href: null }
  ];

  const clientCategories = [
    { value: 'entreprise', label: 'Entreprise' },
    { value: 'association', label: 'Association' },
    { value: 'entrepreneur', label: 'Entrepreneur' },
    { value: 'particulier', label: 'Particulier' }
  ];

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'client_category' && value !== 'particulier') {
      setFormData(prev => ({ ...prev, is_particulier_caf: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.client_category || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setIsSuccess(true);
        toast.success('Demande envoyée avec succès !');
        setFormData({ name: '', email: '', phone: '', client_category: '', service_type: '', message: '', is_particulier_caf: false });
        setTimeout(() => setIsSuccess(false), 5000);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 bg-black relative" data-testid="contact-section">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A55C]/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20 space-y-6">
          <span className="text-sm font-semibold tracking-[0.3em] uppercase text-[#C9A55C]">Contact</span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-white">Parlons de votre projet</h2>
          <p className="text-lg text-white/60">Premier échange gratuit. Réponse rapide dans les 2h.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactInfo.map((info, index) => (
                <div key={index} className="border border-[#C9A55C]/20 p-6 hover:border-[#C9A55C]/50 transition-colors" data-testid={`contact-info-${info.label.toLowerCase()}`}>
                  {info.href ? (
                    <a href={info.href} className="flex items-start gap-4 group">
                      <div className="w-12 h-12 border border-[#C9A55C]/30 flex items-center justify-center group-hover:border-[#C9A55C] transition-colors">
                        <info.icon className="w-5 h-5 text-[#C9A55C]" />
                      </div>
                      <div>
                        <p className="text-sm text-white/50">{info.label}</p>
                        <p className="font-medium text-white group-hover:text-[#C9A55C] transition-colors">{info.value}</p>
                      </div>
                    </a>
                  ) : (
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 border border-[#C9A55C]/30 flex items-center justify-center">
                        <info.icon className="w-5 h-5 text-[#C9A55C]" />
                      </div>
                      <div>
                        <p className="text-sm text-white/50">{info.label}</p>
                        <p className="font-medium text-white">{info.value}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Logo and RDV button */}
            <div className="border border-[#C9A55C]/20 p-8 flex flex-col items-center justify-center gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_service-devis-1/artifacts/32x86mnu_IMG_4011.jpeg"
                alt="HELP ADN"
                className="h-24 w-auto opacity-80"
              />
              <a 
                href="https://tr.ee/stZmBN1GVb"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold rounded-none px-8 py-4 text-sm font-semibold tracking-wider uppercase inline-flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                Prendre rendez-vous
              </a>
              <p className="text-white/50 text-sm text-center">Réservez un créneau en ligne</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="border border-[#C9A55C]/20 p-10">
            <h3 className="text-xl font-serif font-semibold text-white mb-8">Envoyer une demande</h3>

            {isSuccess ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 border border-[#C9A55C] flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-[#C9A55C]" />
                </div>
                <h4 className="text-xl font-serif text-white mb-2">Message envoyé !</h4>
                <p className="text-white/60">Réponse rapide dans les 2h.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="space-y-2">
                  <label className="text-sm text-white/70">Nom complet *</label>
                  <Input name="name" value={formData.name} onChange={handleInputChange} placeholder="Votre nom" className="bg-transparent border-[#C9A55C]/30 text-white rounded-none h-12 focus:border-[#C9A55C]" data-testid="input-name" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm text-white/70">Email *</label>
                    <Input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="email@exemple.com" className="bg-transparent border-[#C9A55C]/30 text-white rounded-none h-12 focus:border-[#C9A55C]" data-testid="input-email" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-white/70">Téléphone</label>
                    <Input name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="06 XX XX XX XX" className="bg-transparent border-[#C9A55C]/30 text-white rounded-none h-12 focus:border-[#C9A55C]" data-testid="input-phone" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-white/70">Vous êtes *</label>
                  <Select value={formData.client_category} onValueChange={(value) => handleSelectChange('client_category', value)}>
                    <SelectTrigger className="bg-transparent border-[#C9A55C]/30 text-white rounded-none h-12" data-testid="select-category">
                      <SelectValue placeholder="Sélectionnez votre profil" />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-[#C9A55C]/30">
                      {clientCategories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-[#C9A55C]/20">{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.client_category === 'particulier' && (
                  <div className="border border-[#C9A55C]/30 bg-[#C9A55C]/5 p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox id="is_particulier_caf" checked={formData.is_particulier_caf} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_particulier_caf: checked }))} className="border-[#C9A55C]/50 data-[state=checked]:bg-[#C9A55C]" data-testid="checkbox-caf" />
                      <div>
                        <label htmlFor="is_particulier_caf" className="text-sm font-medium text-white cursor-pointer">Démarches CAF / CPAM</label>
                        <p className="text-xs text-[#C9A55C]/70 mt-1 flex items-center gap-1"><Info className="w-3 h-3" />Tarifs préférentiels</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm text-white/70">Votre message *</label>
                  <Textarea name="message" value={formData.message} onChange={handleInputChange} placeholder="Décrivez votre besoin..." className="bg-transparent border-[#C9A55C]/30 text-white rounded-none min-h-[120px] focus:border-[#C9A55C]" data-testid="input-message" />
                </div>

                <Button type="submit" disabled={isSubmitting} className="w-full btn-gold rounded-none h-14 font-semibold tracking-wider uppercase" data-testid="submit-button">
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Envoi...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2"><Send className="w-5 h-5" />Envoyer</span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
